import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';

const AttachmentsTab = ({ categoryId, category, currentUserId, canUpload = true }) => {
  const [attachments, setAttachments] = useState([]);
  const [courseAttachments, setCourseAttachments] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState(''); // New state for title

    // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fetchAttachments = async () => {
    try {
      console.log("Fetching attachments for:", categoryId, category);
      const response = await axios.get(`${API_BASE_URL}/attachments/get_attachments.php`, {
        params: {
          user_id: currentUserId,
          category_id: categoryId,
          category: category
        },
        withCredentials: true
      });

       console.log("Attachments response:", response);
      
      // Ensure response data is always an array
      const data = Array.isArray(response.data) ? response.data : [];
      setAttachments(data);
    } catch (err) {
      console.error("Fetch attachments error:", err);
      setError('Failed to fetch attachments: ' + (err.response?.data?.error || err.message));
      setAttachments([]);
    }
  };

    const fetchCourseAttachments = async () => {
    try {
      if (category=='training_documents') {
        console.log("Fetching course attachments for:", categoryId, category);
        const response = await axios.get(`${API_BASE_URL}/attachments/get_attachments.php`, {
          params: {
            user_id: currentUserId,
            category_id: categoryId,
            category: 'training_course_documents'
          },
          withCredentials: true
        });

        console.log("Course Attachments response:", response);
        
        // Ensure response data is always an array
        const data = Array.isArray(response.data) ? response.data : [];
        setCourseAttachments(data);
      }
      else {
        setCourseAttachments([]);
      }
    } catch (err) {
      console.error("Fetch course attachments error:", err);
      setError('Failed to fetch course attachments: ' + (err.response?.data?.error || err.message));
      setCourseAttachments([]);
    }
  };

  useEffect(() => {
    if (categoryId) fetchAttachments();
    if (categoryId) fetchCourseAttachments();
  }, [categoryId, category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    
    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', currentUserId);
    formData.append('category_id', categoryId);
    formData.append('category', category);
    
    const attachmentName = title.trim() || file.name;
    formData.append('name', attachmentName);

    try {
      // 1. Upload the file
      const uploadResponse = await axios.post(
        `${API_BASE_URL}/attachments/upload_attachment.php`, 
        formData, 
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );
      console.log("Upload response:", uploadResponse);

      // 2. Check if upload was successful
      if (uploadResponse.data.status !== 'success') {
        throw new Error(uploadResponse.data.message || 'Upload failed');
      }
      
      // 3. Refresh attachments list
      await fetchAttachments();
      
      // 4. Reset file input
      setFile(null);
      setTitle(''); // Reset title field
      e.target.reset(); // Reset the form to clear file input
       setSuccess('File uploaded successfully!');
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || 'File upload failed');
    } finally {
      setUploading(false);
    }
  };


  /*
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);
    formData.append('category_id', category);
    formData.append('name', file.name);

    try {
      await axios.post(`${API_BASE_URL}/attachments/upload_attachment.php`, 
        formData, 
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );
      // Refresh attachments list
      const response = await axios.get(`${API_BASE_URL}/attachments/get_attachments.php`, {
        params: { user_id: userId, category_id: category },
        withCredentials: true
      });
      setAttachments(response.data);
      setFile(null);
    } catch (err) {
      setError('File upload failed');
    }
  };
*/

  const handleDelete = async (attachmentId, filePath) => {
    const result = await Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this file?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setError('');
    setSuccess('');
    
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/attachments/delete_attachment.php`, 
        {
          data: {
            id: attachmentId,
            path: filePath
          },
          withCredentials: true
        }
      );
      
      if (response.data.status === 'success') {
        setAttachments(attachments.filter(a => a.id !== attachmentId));
        setSuccess('File deleted successfully!');
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete file');
    }
  };
  
  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

    {courseAttachments.length > 0 && (
      <div>
      <h4>Course Documents/Attachments</h4>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>File Name</th>
            <th>Date Uploaded</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {courseAttachments.length > 0 && (
            courseAttachments.map((attachment) => (
              <tr key={attachment.id}>
                <td>{attachment.name}</td>
                <td>{new Date(attachment.date).toLocaleDateString()}</td>
                <td>
                  <a
                    href={`${API_BASE_URL}/attachments/download_attachment.php?id=${attachment.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className='btn btn-primary btn-sm mr-1'
                  >
                    Download
                  </a>

                </td>
              </tr>
            ))
          ) }
        </tbody>
      </Table>
      </div>
      )}

    <div>
      <h4>Training Documents/Attachments</h4>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>File Name</th>
            <th>Date Uploaded</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {attachments.length > 0 ? (
            attachments.map((attachment) => (
              <tr key={attachment.id}>
                <td>{attachment.name}</td>
                <td>{new Date(attachment.date).toLocaleDateString()}</td>
                <td>
                  <a
                    href={`${API_BASE_URL}/attachments/download_attachment.php?id=${attachment.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className='btn btn-primary btn-sm mr-1'
                  >
                    Download
                  </a>
                  {/* Only show delete button for file owner */}
                  {attachment.user_id == currentUserId && (
                    <Button 
                      variant="danger" 
                      size="sm"
                      onClick={() => handleDelete(attachment.id, attachment.path)}
                    >
                      Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3">No attachments found</td>
            </tr>
          )}
        </tbody>
      </Table>
      </div>

      {canUpload && (
        <Form onSubmit={handleSubmit} className="mt-4 mb-4">
         <Form.Group className="mb-3">
          <Form.Label>Title (Optional)</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter title for this file"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={uploading}
          />
          <Form.Text className="text-muted">
            If left blank, filename will be used
          </Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label>File Attachment</Form.Label>
          <Form.Control
            type="file"
            onChange={(e) => setFile(e.target.files[0])}
            required
            disabled={uploading}
          />
        </Form.Group>
        <input type="hidden" name="category" value={category} />
         <input type="hidden" name="category_id" value={categoryId} />
        <Button variant="primary" type="submit" className="mt-2"  disabled={uploading}>
           {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </Form>
      )}

    </div>
  );
};

export default AttachmentsTab;